import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Modal, ListGroup } from 'react-bootstrap';
import {
    BarChart2, Filter, FileText, DollarSign, TrendingUp, ShoppingBag, Package, Users,
    FileSpreadsheet, File, CheckSquare, Square, Calendar, Play, RefreshCw, Receipt
} from 'lucide-react';
import { getAllOrders } from '../api/orders';
import { getAllProducts } from '../api/products';
import { getAllSuppliers } from '../api/partner'; // Vos clients
import { getAllDeliveryNotes } from '../api/orders';
import { getAllInvoices } from '../api/invoices'; // AJOUT: Import des factures
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import useAuth from '../hooks/useAuth';
import Loading from '../components/common/Loading';
import { CSVLink } from 'react-csv';
import html2pdf from 'html2pdf.js';
import { toast } from 'react-toastify'; // AJOUT: Import pour les notifications

const ReportingPage = () => {
    const { isAdmin, isManager } = useAuth();
    const [loading, setLoading] = useState(true);
    const [reportType, setReportType] = useState('sales');
    const [timeFrame, setTimeFrame] = useState('month');
    const [startDate, setStartDate] = useState(getDefaultStartDate('month'));
    const [endDate, setEndDate] = useState(getDefaultEndDate());
    const [orderType, setOrderType] = useState('CLIENT');
    const [reportData, setReportData] = useState([]);
    const [error, setError] = useState('');
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [clients, setClients] = useState([]);
    const [deliveryNotes, setDeliveryNotes] = useState([]);
    const [invoices, setInvoices] = useState([]); // AJOUT: État pour les factures
    const [summaryData, setSummaryData] = useState({
        totalSales: 0,
        orderCount: 0,
        averageOrderValue: 0,
        topProducts: [],
        topClients: []
    });

    // État pour le rapport builder
    const [isGenerating, setIsGenerating] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportType, setExportType] = useState('');
    const [selectedFields, setSelectedFields] = useState({});

    // Couleurs pour les graphiques
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

    // Helper functions
    function getDefaultStartDate(timeFrame) {
        const date = new Date();
        switch (timeFrame) {
            case 'week':
                date.setDate(date.getDate() - 7);
                break;
            case 'month':
                date.setMonth(date.getMonth() - 1);
                break;
            case 'quarter':
                date.setMonth(date.getMonth() - 3);
                break;
            case 'year':
                date.setFullYear(date.getFullYear() - 1);
                break;
            default:
                date.setMonth(date.getMonth() - 1);
        }
        return formatDateForInput(date);
    }

    function getDefaultEndDate() {
        return formatDateForInput(new Date());
    }

    function formatDateForInput(date) {
        return date.toISOString().split('T')[0];
    }

    const formatCurrency = (value, withFcfa = false) => {
        const formatted = new Intl.NumberFormat('fr-FR').format(value);
        return withFcfa ? `${formatted} FCFA` : formatted;
    };

    // ✅ CORRECTION: Fonction pour obtenir le titre du rapport
    const getReportTitle = () => {
        switch (reportType) {
            case 'sales':
                return 'Rapport des Ventes';
            case 'products':
                return 'Performance des Produits';
            case 'clients':
                return 'Performance des Clients';
            case 'invoices':
                return 'Rapport des Factures';
            case 'deliveryNotes':
                return 'Bons de Livraison';
            default:
                return 'Rapport';
        }
    };

    // Définir les champs disponibles pour l'exportation selon le type de rapport
    const getAvailableFields = () => {
        switch (reportType) {
            case 'sales':
                return {
                    orderNumber: 'Numéro de commande',
                    orderDate: 'Date de commande',
                    clientName: 'Client',
                    totalAmount: 'Montant total',
                    status: 'Statut',
                    productCount: 'Nombre de produits',
                    deliveryAddress: 'Adresse de livraison'
                };
            case 'products':
                return {
                    name: 'Produit',
                    reference: 'Référence',
                    category: 'Catégorie',
                    quantitySold: 'Quantité vendue',
                    revenue: 'Chiffre d\'affaires',
                    averagePrice: 'Prix moyen'
                };
            case 'clients':
                return {
                    name: 'Client',
                    email: 'Email',
                    phone: 'Téléphone',
                    orderCount: 'Nombre de commandes',
                    totalSpent: 'Montant total dépensé',
                    averageOrderValue: 'Valeur moyenne des commandes',
                    firstOrder: 'Première commande',
                    lastOrder: 'Dernière commande'
                };
            case 'deliveryNotes':
                return {
                    referenceNumber: 'Référence',
                    deliveryDate: 'Date de livraison',
                    orderNumber: 'Numéro de commande',
                    clientName: 'Client',
                    status: 'Statut',
                    createdBy: 'Créé par',
                    itemCount: 'Nombre d\'articles'
                };
            // AJOUT: Champs pour les factures
            case 'invoices':
                return {
                    invoiceNumber: 'Numéro de facture',
                    issueDate: 'Date d\'émission',
                    dueDate: 'Date d\'échéance',
                    clientName: 'Client',
                    orderNumber: 'Numéro de commande',
                    totalAmount: 'Montant total',
                    montantPaiement: 'Montant payé',
                    remainingAmount: 'Montant restant',
                    status: 'Statut',
                    paymentTerms: 'Conditions de paiement',
                    paidDate: 'Date de paiement'
                };
            default:
                return {};
        }
    };

    // Charger les données
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError('');

                // AJOUT: Inclusion des factures dans le chargement
                const [ordersData, productsData, clientsData, deliveryNotesData, invoicesData] = await Promise.all([
                    getAllOrders(),
                    getAllProducts(),
                    getAllSuppliers(), // Vos clients
                    getAllDeliveryNotes(),
                    getAllInvoices() // AJOUT: Chargement des factures
                ]);

                setOrders(ordersData);
                setProducts(productsData);
                setClients(clientsData);
                setDeliveryNotes(deliveryNotesData);
                setInvoices(invoicesData); // AJOUT: Stocker les factures

                // Initialiser les champs sélectionnés
                initializeSelectedFields();
            } catch (err) {
                setError('Erreur lors du chargement des données. Veuillez réessayer.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Initialiser les champs sélectionnés
    const initializeSelectedFields = () => {
        const fields = getAvailableFields();
        const initialSelectedFields = {};
        Object.keys(fields).forEach(field => {
            initialSelectedFields[field] = true;
        });
        setSelectedFields(initialSelectedFields);
    };

    // ✅ CORRECTION: Mettre à jour les champs sélectionnés quand le type de rapport change
    useEffect(() => {
        initializeSelectedFields();
    }, [reportType]);

    // Générer le rapport automatiquement au chargement
    useEffect(() => {
        if (orders.length > 0 && products.length > 0) {
            generateReport();
        }
    }, [orders, products, clients, deliveryNotes, invoices, reportType, timeFrame, startDate, endDate]); // AJOUT: invoices dans les dépendances

    // Fonctions utilitaires
    const extractPartnerFromOrder = (order) => {
        if (order.partner) {
            return {
                id: order.partner.id,
                name: order.partner.name || 'Client Inconnu',
                email: order.partner.email || ''
            };
        }
        return {
            id: 'unknown',
            name: 'Client Inconnu',
            email: ''
        };
    };

    const extractProductsFromOrder = (order) => {
        if (!order.items || !Array.isArray(order.items)) {
            return [];
        }
        return order.items.map(item => ({
            productId: item.product?.id || item.productId,
            productName: item.product?.name || 'Produit Inconnu',
            quantity: item.quantity || 0,
            unitPrice: item.unitPrice || 0,
            total: item.total || (item.quantity * item.unitPrice) || 0
        }));
    };

    const calculateOrderTotal = (order) => {
        return order.totalAmount || 0;
    };

    // Fonction pour traduire les statuts en français
    const translateStatus = (status) => {
        const statusTranslations = {
            'PENDING': 'En attente',
            'CONFIRMED': 'Confirmée',
            'DELIVERED': 'Livrée',
            'REJECTED': 'Rejetée',
            'CANCELLED': 'Annulée',
            // AJOUT: Statuts des factures
            'PAID': 'Payée',
            'PART_PAY': 'Partiellement payée',
            'OVERDUE': 'En retard'
        };
        return statusTranslations[status] || status;
    };

    // AJOUT: Génération du rapport pour les factures
    const generateInvoicesReport = () => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        const filteredInvoices = invoices.filter(invoice => {
            const issueDate = new Date(invoice.issueDate);
            return issueDate >= start && issueDate <= end;
        });

        return filteredInvoices.map(invoice => ({
            id: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            issueDate: new Date(invoice.issueDate).toLocaleDateString('fr-FR'),
            dueDate: new Date(invoice.dueDate).toLocaleDateString('fr-FR'),
            clientName: invoice.client?.name || 'Client Inconnu',
            orderNumber: invoice.order?.orderNumber || '-',
            totalAmount: invoice.totalAmount || 0,
            montantPaiement: invoice.montantPaiement || 0,
            remainingAmount: (invoice.totalAmount || 0) - (invoice.montantPaiement || 0),
            status: translateStatus(invoice.status),
            paymentTerms: invoice.paymentTerms ? `${invoice.paymentTerms} jours` : '-',
            paidDate: invoice.paidDate ? new Date(invoice.paidDate).toLocaleDateString('fr-FR') : '-'
        }));
    };

    // AJOUT: Calcul du résumé pour les factures
    const calculateInvoicesSummary = (data) => {
        const totalAmount = data.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
        const totalPaid = data.reduce((sum, invoice) => sum + (invoice.montantPaiement || 0), 0);
        
        return { 
            totalSales: totalAmount, 
            totalPaid,
            totalRemaining: totalAmount - totalPaid,
            orderCount: data.length, 
            averageOrderValue: data.length > 0 ? totalAmount / data.length : 0, 
            topProducts: [], 
            topClients: []
        };
    };

    // Génération du rapport
    const generateReport = () => {
        setIsGenerating(true);
        
        setTimeout(() => {
            const start = new Date(startDate);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);

            // Filtrer les commandes (seulement ventes CLIENT)
            let filteredOrders = orders.filter(order => {
                const orderDate = new Date(order.orderDate || order.createdAt);
                return orderDate >= start && orderDate <= end && order.type === 'CLIENT';
            });

            let data = [];
            let summary = {};

            switch (reportType) {
                case 'sales':
                    data = generateSalesReport(filteredOrders);
                    summary = calculateSalesSummary(filteredOrders);
                    break;
                case 'products':
                    data = generateProductsReport(filteredOrders);
                    summary = calculateProductsSummary(data);
                    break;
                case 'clients':
                    data = generateClientsReport(filteredOrders);
                    summary = calculateClientsSummary(data);
                    break;
                case 'deliveryNotes':
                    data = generateDeliveryNotesReport();
                    summary = calculateDeliveryNotesSummary(data);
                    break;
                // AJOUT: Cas pour les factures
                case 'invoices':
                    data = generateInvoicesReport();
                    summary = calculateInvoicesSummary(data);
                    break;
                default:
                    data = [];
                    summary = { totalSales: 0, orderCount: 0, averageOrderValue: 0, topProducts: [], topClients: [] };
            }

            setReportData(data);
            setSummaryData(summary);
            setIsGenerating(false);
        }, 1000);
    };

    const generateSalesReport = (filteredOrders) => {
        return filteredOrders.map(order => {
            const client = extractPartnerFromOrder(order);
            const orderProducts = extractProductsFromOrder(order);
            
            return {
                id: order.id,
                orderNumber: order.orderNumber,
                orderDate: new Date(order.orderDate || order.createdAt).toLocaleDateString('fr-FR'),
                clientName: client.name,
                totalAmount: calculateOrderTotal(order),
                status: translateStatus(order.status),
                productCount: orderProducts.length,
                deliveryAddress: order.deliveryAddress || '-'
            };
        });
    };

    const generateProductsReport = (filteredOrders) => {
        const productSales = {};
        
        filteredOrders.forEach(order => {
            const orderProducts = extractProductsFromOrder(order);
            orderProducts.forEach(item => {
                const productId = item.productId;
                if (!productSales[productId]) {
                    const productInfo = products.find(p => p.id === productId) || {};
                    productSales[productId] = {
                        id: productId,
                        name: item.productName,
                        reference: productInfo.sku || productInfo.reference || '-',
                        category: productInfo.category || '-',
                        quantitySold: 0,
                        revenue: 0,
                        sales: []
                    };
                }
                
                productSales[productId].quantitySold += item.quantity;
                productSales[productId].revenue += item.total;
                productSales[productId].sales.push(item);
            });
        });

        return Object.values(productSales).map(product => ({
            ...product,
            averagePrice: product.quantitySold > 0 ? product.revenue / product.quantitySold : 0
        })).sort((a, b) => b.revenue - a.revenue);
    };

    const generateClientsReport = (filteredOrders) => {
        const clientStats = {};
        
        filteredOrders.forEach(order => {
            const client = extractPartnerFromOrder(order);
            const clientId = client.id;
            
            if (!clientStats[clientId]) {
                const clientInfo = clients.find(c => c.id === clientId) || {};
                clientStats[clientId] = {
                    id: clientId,
                    name: client.name,
                    email: client.email || clientInfo.email || '',
                    phone: clientInfo.phone || '',
                    orderCount: 0,
                    totalSpent: 0,
                    orders: []
                };
            }
            
            clientStats[clientId].orderCount += 1;
            clientStats[clientId].totalSpent += calculateOrderTotal(order);
            clientStats[clientId].orders.push(new Date(order.orderDate || order.createdAt));
        });

        return Object.values(clientStats).map(client => ({
            ...client,
            averageOrderValue: client.orderCount > 0 ? client.totalSpent / client.orderCount : 0,
            firstOrder: client.orders.length > 0 ? new Date(Math.min(...client.orders)).toLocaleDateString('fr-FR') : '',
            lastOrder: client.orders.length > 0 ? new Date(Math.max(...client.orders)).toLocaleDateString('fr-FR') : ''
        })).sort((a, b) => b.totalSpent - a.totalSpent);
    };

    const generateDeliveryNotesReport = () => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        // Vérifier que deliveryNotes est un tableau
        const notesArray = Array.isArray(deliveryNotes) ? deliveryNotes : 
                          deliveryNotes?.data ? deliveryNotes.data : 
                          [];

        return notesArray.filter(note => {
            const deliveryDate = new Date(note.deliveryDate);
            return deliveryDate >= start && deliveryDate <= end;
        }).map(note => ({
            id: note.id,
            referenceNumber: note.referenceNumber,
            deliveryDate: new Date(note.deliveryDate).toLocaleDateString('fr-FR'),
            orderNumber: note.orderReference || note.order?.orderNumber || '-',
            clientName: note.clientName || '-',
            status: translateStatus(note.status) || 'Livrée',
            createdBy: note.createdByName || '-',
            itemCount: note.items ? note.items.length : 0
        }));
    };

    // Calculs des résumés
    const calculateSalesSummary = (filteredOrders) => {
        const totalSales = filteredOrders.reduce((sum, order) => sum + calculateOrderTotal(order), 0);
        const orderCount = filteredOrders.length;
        const averageOrderValue = orderCount > 0 ? totalSales / orderCount : 0;

        // Calculer les top produits et clients
        const productSales = {};
        const clientSales = {};

        filteredOrders.forEach(order => {
            const client = extractPartnerFromOrder(order);
            const orderProducts = extractProductsFromOrder(order);
            const orderTotal = calculateOrderTotal(order);

            // Stats clients
            if (!clientSales[client.id]) {
                clientSales[client.id] = { name: client.name, totalSpent: 0, orderCount: 0 };
            }
            clientSales[client.id].totalSpent += orderTotal;
            clientSales[client.id].orderCount += 1;

            // Stats produits
            orderProducts.forEach(item => {
                if (!productSales[item.productId]) {
                    productSales[item.productId] = { 
                        name: item.productName, 
                        quantity: 0, 
                        revenue: 0 
                    };
                }
                productSales[item.productId].quantity += item.quantity;
                productSales[item.productId].revenue += item.total;
            });
        });

        const topProducts = Object.values(productSales).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
        const topClients = Object.values(clientSales).sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 5);

        return { totalSales, orderCount, averageOrderValue, topProducts, topClients };
    };

    const calculateProductsSummary = (data) => {
        const totalRevenue = data.reduce((sum, product) => sum + product.revenue, 0);
        const topProducts = data.slice(0, 5);
        return { totalSales: totalRevenue, orderCount: data.length, averageOrderValue: 0, topProducts, topClients: [] };
    };

    const calculateClientsSummary = (data) => {
        const totalRevenue = data.reduce((sum, client) => sum + client.totalSpent, 0);
        const topClients = data.slice(0, 5);
        return { totalSales: totalRevenue, orderCount: data.length, averageOrderValue: 0, topProducts: [], topClients };
    };

    const calculateDeliveryNotesSummary = (data) => {
        // Vérifier que data est un tableau
        const dataArray = Array.isArray(data) ? data : [];
        return { 
            totalSales: 0, 
            orderCount: dataArray.length, 
            averageOrderValue: 0, 
            topProducts: [], 
            topClients: [] 
        };
    };

    const handleTimeFrameChange = (e) => {
        const newTimeFrame = e.target.value;
        setTimeFrame(newTimeFrame);
        setStartDate(getDefaultStartDate(newTimeFrame));
    };

    const toggleFieldSelection = (field) => {
        setSelectedFields({
            ...selectedFields,
            [field]: !selectedFields[field]
        });
    };

    // ✅ CORRECTION: Fonction pour préparer les données d'export
    const prepareExportData = (data, fields) => {
        console.log('Preparing export data:', {
            reportDataLength: data.length,
            selectedFieldKeys: Object.keys(fields).filter(key => fields[key]),
            sampleRow: data[0]
        });

        // Déterminer les champs disponibles selon le type de rapport
        const availableFields = getAvailableFields();

        // Filtrer et mapper les données
        return data.map(row => {
            const exportRow = {};
            
            Object.keys(fields).forEach(field => {
                if (fields[field] && availableFields[field]) {
                    let value = row[field];
                    
                    // Formatage spécifique
                    if (field === 'orderDate' && value) {
                        value = new Date(value).toLocaleDateString('fr-FR');
                    } else if (['totalAmount', 'revenue', 'totalRevenue', 'averagePrice', 'totalSpent', 'montantPaiement', 'remainingAmount'].includes(field) && value) {
                        value = `${new Intl.NumberFormat('fr-FR').format(value)} FCFA`;
                    } else if (field === 'status' && value) {
                        const statusMap = {
                            'PENDING': 'En attente',
                            'CONFIRMED': 'Confirmée',
                            'DELIVERED': 'Livrée',
                            'REJECTED': 'Rejetée'
                        };
                        value = statusMap[value] || value;
                    }
                    
                    exportRow[availableFields[field]] = value || '-';
                }
            });
            
            return exportRow;
        });
    };

    const exportToCSV = () => {
        if (reportData.length === 0) {
            toast.error("Aucune donnée à exporter");
            return;
        }
        
        console.log('Exporting CSV with data:', {
            reportDataLength: reportData.length,
            csvDataLength: csvData.length,
            headers: csvHeaders
        });
        
        // Le CSVLink gérera l'export automatiquement
        setShowExportModal(false);
        toast.success('Export CSV lancé');
    };

    // ✅ CORRECTION: Fonction de génération PDF corrigée
    const generatePDF = async () => {
        console.log("=== DÉBUT DIAGNOSTIC PDF ===");
        console.log("1. reportData:", reportData);
        console.log("2. reportData.length:", reportData.length);
        console.log("3. selectedFields:", selectedFields);
        console.log("4. reportType:", reportType);
        
        if (!reportData || reportData.length === 0) {
            toast.error('Aucune donnée à exporter');
            return;
        }

        try {
            // ✅ CORRECTION : Déterminer les champs disponibles selon le type de rapport
            const availableFields = getAvailableFields();
            
            console.log("5. Titre du rapport:", getReportTitle());
            console.log("6. fields disponibles:", availableFields);

            // ✅ CORRECTION : Filtrer les champs sélectionnés selon le type de rapport
            const relevantSelectedFields = Object.keys(selectedFields)
                .filter(field => selectedFields[field] && availableFields[field])
                .reduce((acc, field) => {
                    acc[field] = selectedFields[field];
                    return acc;
                }, {});

            const selectedFieldKeys = Object.keys(relevantSelectedFields);
            console.log("7. selectedFieldKeys (filtrés):", selectedFieldKeys);

            if (selectedFieldKeys.length === 0) {
                toast.error('Aucun champ valide sélectionné pour ce type de rapport');
                return;
            }

            // Créer les colonnes du tableau
            const tableColumns = selectedFieldKeys.map(field => availableFields[field]);
            console.log("8. tableColumns:", tableColumns);

            // Créer le contenu HTML
            let htmlContent = `
            <div style="font-family: Arial, sans-serif; padding: 20px;">
                <div style="text-align: center; margin-bottom: 30px;">
                    <h1 style="color: #333; margin-bottom: 10px;">${getReportTitle()}</h1>
                    <p style="color: #666; margin: 0;">Généré le ${new Date().toLocaleDateString('fr-FR')}</p>
                </div>
                
                <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
                    <thead>
                        <tr style="background-color: #f8f9fa;">
            `;

            // Ajouter les en-têtes
            tableColumns.forEach(column => {
                htmlContent += `<th style="border: 1px solid #ddd; padding: 12px; text-align: left; font-weight: bold; color: #333;">${column}</th>`;
            });

            htmlContent += `
                        </tr>
                    </thead>
                    <tbody>
            `;

            console.log("9. En-têtes de tableau créés");
            console.log("10. Création des lignes de données...");

            // Ajouter les données
            reportData.forEach((row, index) => {
                console.log(`11.${index} - Ligne de données:`, row);
                
                htmlContent += '<tr>';
                selectedFieldKeys.forEach((field, fieldIndex) => {
                    let value = row[field];
                    console.log(`11.${index}.${fieldIndex} - ${field}:`, value);
                    
                    // Formatage spécifique selon le champ
                    if (field === 'orderDate' && value) {
                        value = new Date(value).toLocaleDateString('fr-FR');
                    } else if (['totalAmount', 'revenue', 'totalRevenue', 'averagePrice', 'totalSpent', 'montantPaiement', 'remainingAmount'].includes(field) && value) {
                        value = new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
                    } else if (field === 'status' && value) {
                        const statusMap = {
                            'PENDING': 'En attente',
                            'CONFIRMED': 'Confirmée',
                            'DELIVERED': 'Livrée',
                            'REJECTED': 'Rejetée'
                        };
                        value = statusMap[value] || value;
                    }
                    
                    htmlContent += `<td style="border: 1px solid #ddd; padding: 8px;">${value || '-'}</td>`;
                });
                htmlContent += '</tr>';
            });

            htmlContent += `
                    </tbody>
                </table>
                
                <div style="margin-top: 30px; text-align: center; color: #666; font-size: 12px;">
                    <p>Rapport généré automatiquement par le système de gestion</p>
                </div>
            </div>
            `;

            console.log("12. Tableau HTML créé");
            console.log("13. Contenu HTML final créé");

            // Créer un élément temporaire pour le PDF
            const element = document.createElement('div');
            element.innerHTML = htmlContent;
            element.style.width = '100%';
            document.body.appendChild(element);

            console.log("14. Element ajouté au DOM");

            // Configuration pour html2pdf
            const opt = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: `${getReportTitle().toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { 
                    scale: 1.5,
                    useCORS: true,
                    letterRendering: true,
                    allowTaint: false
                },
                jsPDF: { 
                    unit: 'in', 
                    format: 'a4', 
                    orientation: selectedFieldKeys.length > 4 ? 'landscape' : 'portrait'
                }
            };

            console.log("15. Options PDF:", opt);
            console.log("16. Début génération PDF...");

            // Générer le PDF
            await html2pdf().set(opt).from(element).save();

            console.log("✅ PDF généré avec succès");
            toast.success('PDF généré avec succès');

            // Nettoyer
            document.body.removeChild(element);

        } catch (error) {
            console.error('❌ Erreur lors de la génération du PDF:', error);
            toast.error('Erreur lors de la génération du PDF: ' + error.message);
        }
        
        console.log("=== FIN DIAGNOSTIC PDF ===");
    };

    // ✅ CORRECTION: Fonction exportToPDF mise à jour
    const exportToPDF = () => {
        generatePDF();
        setShowExportModal(false);
    };

    // Render des graphiques (tous en BarChart)
    const renderChart = () => {
        if (reportData.length === 0) {
            return (
                <div className="text-center py-5">
                    <BarChart2 size={48} className="text-muted mb-3" />
                    <p>Aucune donnée disponible pour la période sélectionnée</p>
                </div>
            );
        }

        switch (reportType) {
            case 'sales':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={reportData.slice(0, 20)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="orderDate" />
                            <YAxis />
                            <Tooltip formatter={(value) => [formatCurrency(value), 'Montant']} />
                            <Legend />
                            <Bar dataKey="totalAmount" name="Montant" fill="#8884d8" />
                        </BarChart>
                    </ResponsiveContainer>
                );

            case 'products':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={reportData.slice(0, 10)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip formatter={(value, name) => [
                                name === 'revenue' ? formatCurrency(value) : value,
                                name === 'revenue' ? 'Chiffre d\'affaires' : 'Quantité'
                            ]} />
                            <Legend />
                            <Bar dataKey="revenue" name="Chiffre d'affaires" fill="#8884d8" />
                            <Bar dataKey="quantitySold" name="Quantité vendue" fill="#82ca9d" />
                        </BarChart>
                    </ResponsiveContainer>
                );

            case 'clients':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={reportData.slice(0, 10)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip formatter={(value) => [formatCurrency(value), 'Montant dépensé']} />
                            <Legend />
                            <Bar dataKey="totalSpent" name="Montant dépensé" fill="#8884d8" />
                        </BarChart>
                    </ResponsiveContainer>
                );

            // AJOUT: Graphique pour les factures
            case 'invoices':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={reportData.slice(0, 20)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="issueDate" />
                            <YAxis />
                            <Tooltip formatter={(value, name) => [
                                formatCurrency(value),
                                name === 'totalAmount' ? 'Montant total' : 
                                name === 'montantPaiement' ? 'Montant payé' : 'Montant restant'
                            ]} />
                            <Legend />
                            <Bar dataKey="totalAmount" name="Montant total" fill="#8884d8" />
                            <Bar dataKey="montantPaiement" name="Montant payé" fill="#82ca9d" />
                            <Bar dataKey="remainingAmount" name="Montant restant" fill="#ffc658" />
                        </BarChart>
                    </ResponsiveContainer>
                );

            case 'deliveryNotes':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={reportData.slice(0, 20)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="deliveryDate" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="itemCount" name="Nombre d'articles" fill="#8884d8" />
                        </BarChart>
                    </ResponsiveContainer>
                );

            default:
                return null;
        }
    };

    if (!isAdmin && !isManager) {
        return (
            <Container>
                <Alert variant="danger" className="mt-4">
                    Vous n'êtes pas autorisé à accéder à cette page.
                </Alert>
            </Container>
        );
    }

    if (loading) {
        return <Loading message="Chargement des données de reporting..." />;
    }

    // ✅ CORRECTION: Préparer les données CSV - Calculer dynamiquement
    const csvData = reportData.length > 0 ? prepareExportData(reportData, selectedFields) : [];
    const csvHeaders = Object.keys(getAvailableFields())
        .filter(key => selectedFields[key])
        .map(key => ({
            label: getAvailableFields()[key],
            key: getAvailableFields()[key] // Utiliser le label comme clé pour correspondre à prepareExportData
        }));

    return (
        <Container fluid>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="h3">Générateur de Rapports</h1>
                <div>
                    <Button
                        variant="outline-success"
                        className="me-2"
                        onClick={() => { setExportType('csv'); setShowExportModal(true); }}
                    >
                        <FileSpreadsheet size={16} className="me-1" />
                        CSV
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={() => { setExportType('pdf'); setShowExportModal(true); }}
                    >
                        <File size={16} className="me-1" />
                        PDF
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* AJOUT: Métriques spécifiques aux factures */}
            {reportType === 'invoices' && (
                <Row className="mb-4">
                    <Col md={3} className="mb-3">
                        <Card className="shadow-sm h-100">
                            <Card.Body className="d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="rounded-circle bg-primary bg-opacity-25 p-2 me-2">
                                        <Receipt size={20} className="text-primary" />
                                    </div>
                                    <h6 className="mb-0">Total Factures</h6>
                                </div>
                                <h3 className="mt-auto">{summaryData.orderCount}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                        <Card className="shadow-sm h-100">
                            <Card.Body className="d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="rounded-circle bg-success bg-opacity-25 p-2 me-2">
                                        <DollarSign size={20} className="text-success" />
                                    </div>
                                    <h6 className="mb-0">Montant Total</h6>
                                </div>
                                <h3 className="mt-auto">{formatCurrency(summaryData.totalSales)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                        <Card className="shadow-sm h-100">
                            <Card.Body className="d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="rounded-circle bg-info bg-opacity-25 p-2 me-2">
                                        <TrendingUp size={20} className="text-info" />
                                    </div>
                                    <h6 className="mb-0">Montant Payé</h6>
                                </div>
                                <h3 className="mt-auto">{formatCurrency(summaryData.totalPaid || 0)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} className="mb-3">
                        <Card className="shadow-sm h-100">
                            <Card.Body className="d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="rounded-circle bg-warning bg-opacity-25 p-2 me-2">
                                        <FileText size={20} className="text-warning" />
                                    </div>
                                    <h6 className="mb-0">Montant Restant</h6>
                                </div>
                                <h3 className="mt-auto">{formatCurrency(summaryData.totalRemaining || 0)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Métriques de résumé adaptées selon le type de rapport */}
            <Row className="mb-4">
                {reportType === 'sales' && (
                    <>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-success bg-opacity-25 p-2 me-2">
                                            <FileText size={20} className="text-success" />
                                        </div>
                                        <h6 className="mb-0">Total Commandes</h6>
                                    </div>
                                    <h3 className="mt-auto">{summaryData.orderCount}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-primary bg-opacity-25 p-2 me-2">
                                            <DollarSign size={20} className="text-primary" />
                                        </div>
                                        <h6 className="mb-0">Ventes Totales</h6>
                                    </div>
                                    <h3 className="mt-auto">{formatCurrency(summaryData.totalSales)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-warning bg-opacity-25 p-2 me-2">
                                            <Package size={20} className="text-warning" />
                                        </div>
                                        <h6 className="mb-0">Meilleur Produit</h6>
                                    </div>
                                    <h4 className="mt-auto" style={{ fontSize: '1rem' }}>
                                        {summaryData.topProducts.length > 0 
                                            ? summaryData.topProducts[0].name 
                                            : 'Aucun'}
                                    </h4>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-info bg-opacity-25 p-2 me-2">
                                            <TrendingUp size={20} className="text-info" />
                                        </div>
                                        <h6 className="mb-0">Valeur Moyenne</h6>
                                    </div>
                                    <h3 className="mt-auto">{formatCurrency(summaryData.averageOrderValue)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}

                {reportType === 'products' && (
                    <>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-primary bg-opacity-25 p-2 me-2">
                                            <Package size={20} className="text-primary" />
                                        </div>
                                        <h6 className="mb-0">Total Produits</h6>
                                    </div>
                                    <h3 className="mt-auto">{summaryData.orderCount}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-success bg-opacity-25 p-2 me-2">
                                            <DollarSign size={20} className="text-success" />
                                        </div>
                                        <h6 className="mb-0">CA Total</h6>
                                    </div>
                                    <h3 className="mt-auto">{formatCurrency(summaryData.totalSales)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-warning bg-opacity-25 p-2 me-2">
                                            <TrendingUp size={20} className="text-warning" />
                                        </div>
                                        <h6 className="mb-0">Produit Top</h6>
                                    </div>
                                    <h4 className="mt-auto" style={{ fontSize: '1rem' }}>
                                        {summaryData.topProducts.length > 0 
                                            ? summaryData.topProducts[0].name 
                                            : 'Aucun'}
                                    </h4>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-info bg-opacity-25 p-2 me-2">
                                            <ShoppingBag size={20} className="text-info" />
                                        </div>
                                        <h6 className="mb-0">Quantité Totale</h6>
                                    </div>
                                    <h3 className="mt-auto">
                                        {summaryData.topProducts.reduce((total, product) => total + (product.quantity || 0), 0)}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}

                {reportType === 'clients' && (
                    <>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-primary bg-opacity-25 p-2 me-2">
                                            <Users size={20} className="text-primary" />
                                        </div>
                                        <h6 className="mb-0">Total Clients</h6>
                                    </div>
                                    <h3 className="mt-auto">{summaryData.orderCount}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-success bg-opacity-25 p-2 me-2">
                                            <DollarSign size={20} className="text-success" />
                                        </div>
                                        <h6 className="mb-0">CA Total</h6>
                                    </div>
                                    <h3 className="mt-auto">{formatCurrency(summaryData.totalSales)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-warning bg-opacity-25 p-2 me-2">
                                            <TrendingUp size={20} className="text-warning" />
                                        </div>
                                        <h6 className="mb-0">Meilleur Client</h6>
                                    </div>
                                    <h4 className="mt-auto" style={{ fontSize: '1rem' }}>
                                        {summaryData.topClients.length > 0 
                                            ? summaryData.topClients[0].name 
                                            : 'Aucun'}
                                    </h4>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-info bg-opacity-25 p-2 me-2">
                                            <FileText size={20} className="text-info" />
                                        </div>
                                        <h6 className="mb-0">Commandes Totales</h6>
                                    </div>
                                    <h3 className="mt-auto">
                                        {summaryData.topClients.reduce((total, client) => total + (client.orderCount || 0), 0)}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}

                {reportType === 'deliveryNotes' && (
                    <>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-primary bg-opacity-25 p-2 me-2">
                                            <Calendar size={20} className="text-primary" />
                                        </div>
                                        <h6 className="mb-0">Total Livraisons</h6>
                                    </div>
                                    <h3 className="mt-auto">{summaryData.orderCount}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-success bg-opacity-25 p-2 me-2">
                                            <Package size={20} className="text-success" />
                                        </div>
                                        <h6 className="mb-0">Articles Livrés</h6>
                                    </div>
                                    <h3 className="mt-auto">
                                        {reportData.reduce((total, note) => total + (note.itemCount || 0), 0)}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-warning bg-opacity-25 p-2 me-2">
                                            <TrendingUp size={20} className="text-warning" />
                                        </div>
                                        <h6 className="mb-0">Moyenne/Livraison</h6>
                                    </div>
                                    <h3 className="mt-auto">
                                        {reportData.length > 0 
                                            ? Math.round(reportData.reduce((total, note) => total + (note.itemCount || 0), 0) / reportData.length)
                                            : 0}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="rounded-circle bg-info bg-opacity-25 p-2 me-2">
                                            <FileText size={20} className="text-info" />
                                        </div>
                                        <h6 className="mb-0">Statut</h6>
                                    </div>
                                    <h4 className="mt-auto" style={{ fontSize: '1rem' }}>
                                        Livrées
                                    </h4>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}
            </Row>

            {/* Configuration du rapport */}
            <Row className="mb-4">
                <Col md={12}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Configuration du Rapport</h5>
                                <div className="d-flex align-items-center">
                                    <Filter size={16} className="me-1 text-muted" />
                                    <span className="text-muted small">Personnaliser votre rapport</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Form className="row g-3">
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>Type de Rapport</Form.Label>
                                        <Form.Select
                                            value={reportType}
                                            onChange={(e) => setReportType(e.target.value)}
                                        >
                                            <option value="sales">Ventes</option>
                                            <option value="products">Produits</option>
                                            <option value="clients">Clients</option>
                                            <option value="invoices">Factures</option> {/* AJOUT: Option factures */}
                                            <option value="deliveryNotes">Bons de livraison</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>Période</Form.Label>
                                        <Form.Select
                                            value={timeFrame}
                                            onChange={handleTimeFrameChange}
                                        >
                                            <option value="week">Semaine</option>
                                            <option value="month">Mois</option>
                                            <option value="quarter">Trimestre</option>
                                            <option value="year">Année</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Date de Début</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Date de Fin</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="primary"
                                        onClick={generateReport}
                                        disabled={isGenerating}
                                        className="w-100"
                                    >
                                        {isGenerating ? <RefreshCw className="spin me-1" size={16} /> : <Play className="me-1" size={16} />}
                                        {isGenerating ? 'Génération...' : 'Générer'}
                                    </Button>
                                </Col>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Sélection des champs */}
            <Row className="mb-4">
                <Col md={12}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Champs à inclure dans l'export</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                {Object.entries(getAvailableFields()).map(([key, label]) => (
                                    <Col md={3} key={key} className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id={`field-${key}`}
                                            label={label}
                                            checked={selectedFields[key] || false}
                                            onChange={() => toggleFieldSelection(key)}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Graphique */}
            {reportData.length > 0 && (
                <Row className="mb-4">
                    <Col md={12}>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">
                                    {getReportTitle()}
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                {renderChart()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Tableau de résultats */}
            {reportData.length > 0 && (
                <Row className="mb-4">
                    <Col md={12}>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="mb-0">Résultats du rapport</h5>
                                        <small className="text-muted">{reportData.length} enregistrement(s) trouvé(s)</small>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Table responsive className="mb-0">
                                    <thead>
                                        <tr>
                                            {Object.entries(getAvailableFields()).map(([key, label]) => (
                                                <th key={key}>{label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.slice(0, 50).map((row, index) => (
                                            <tr key={index}>
                                                {Object.keys(getAvailableFields()).map(key => {
                                                    let value = row[key];
                                                    if (key.includes('Amount') || key.includes('Price') || key.includes('revenue') || key.includes('totalSpent') || key.includes('montantPaiement') || key.includes('remainingAmount')) {
                                                        value = typeof value === 'number' ? formatCurrency(value) : value;
                                                    }
                                                    return <td key={key}>{value || '-'}</td>;
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                {reportData.length > 50 && (
                                    <div className="text-center py-3 text-muted">
                                        Affichage des 50 premiers résultats. Exportez pour voir tous les résultats.
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Top 5 Tables */}
            <Row>
                <Col md={6} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Top 5 Produits</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Produit</th>
                                        <th className="text-end">Quantité</th>
                                        <th className="text-end">Chiffre d'affaires</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryData.topProducts.length > 0 ? (
                                        summaryData.topProducts.map((product, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <ShoppingBag size={16} className="me-2 text-muted" />
                                                        <span>{product.name}</span>
                                                    </div>
                                                </td>
                                                <td className="text-end">{product.quantity}</td>
                                                <td className="text-end">{formatCurrency(product.revenue)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="text-center py-3">
                                                Aucune donnée disponible
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Top 5 Clients</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Client</th>
                                        <th className="text-end">Commandes</th>
                                        <th className="text-end">Montant Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryData.topClients.length > 0 ? (
                                        summaryData.topClients.map((client, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Users size={16} className="me-2 text-muted" />
                                                        <span>{client.name}</span>
                                                    </div>
                                                </td>
                                                <td className="text-end">{client.orderCount}</td>
                                                <td className="text-end">{formatCurrency(client.totalSpent)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="text-center py-3">
                                                Aucune donnée disponible
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal pour l'exportation */}
            <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {exportType === 'csv' ? 'Exporter en CSV' : 'Exporter en PDF'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Sélectionnez les champs à inclure dans l'exportation :</p>
                    <ListGroup>
                        {Object.entries(getAvailableFields()).map(([key, label]) => (
                            <ListGroup.Item
                                key={key}
                                action
                                onClick={() => toggleFieldSelection(key)}
                                className="d-flex align-items-center"
                            >
                                {selectedFields[key] ?
                                    <CheckSquare size={18} className="me-2 text-primary" /> :
                                    <Square size={18} className="me-2" />
                                }
                                {label}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                        Annuler
                    </Button>
                    {exportType === 'csv' ? (
                        <CSVLink
                            data={reportData.length > 0 ? prepareExportData(reportData, selectedFields) : []}
                            headers={csvHeaders}
                            filename={`rapport_${reportType}_${new Date().toISOString().split('T')[0]}.csv`}
                            className="btn btn-primary"
                            onClick={exportToCSV}
                            target="_blank"
                        >
                            <FileSpreadsheet size={16} className="me-1" />
                            Exporter CSV
                        </CSVLink>
                    ) : (
                        <Button variant="primary" onClick={exportToPDF}>
                            <File size={16} className="me-1" />
                            Exporter PDF
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ReportingPage;